import numeral from "numeral";
import { Order, OrderItem } from "../models/Order";
import { Override } from "./type";

export function gtm(gtmId: string) {
  const str =
    "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f); })(window, document, 'script', 'dataLayer', '" +
    gtmId +
    "');";
  const script = document.createElement("script");
  script.innerHTML = str;
  document.head.insertBefore(script, document.head.childNodes[0]);
}

export function setUserID(userId: number | undefined) {
  if (window.dataLayer) {
    window.dataLayer.push({
      userId,
    });
  }
}

interface GTMProductPageViewParams {
  page: "Product Details";
  productName: string;
  productUrl: string;
  productSku: string;
}

interface GTMCategoryPageViewParams {
  page: "Subcategory";
  categoryName: string;
}

interface GTMArticlePageViewParams {
  page: "Article Page";
  articleTitle: string;
}

type DefinedGTMPageViewParams =
  | GTMProductPageViewParams
  | GTMCategoryPageViewParams
  | GTMArticlePageViewParams;

type DefinedPages = DefinedGTMPageViewParams["page"];

type OtherGTMPageViewParams<T extends string> = T extends DefinedPages
  ? never
  : { page: T };

type GTMPageViewParams<T extends string> =
  | DefinedGTMPageViewParams
  | OtherGTMPageViewParams<T>;

type GTMPageViewParamsKeys = Exclude<
  keyof (GTMProductPageViewParams &
    GTMCategoryPageViewParams &
    GTMArticlePageViewParams),
  "page"
>;

type ResetGTMPageViewParams = { [key in GTMPageViewParamsKeys]: undefined };

const resetGTMPageViewParams: ResetGTMPageViewParams = {
  productName: undefined,
  productUrl: undefined,
  productSku: undefined,
  categoryName: undefined,
  articleTitle: undefined,
};

export function pageView<T extends string>({
  page,
  ...params
}: GTMPageViewParams<T>) {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: "pageView",
      page,
      ...resetGTMPageViewParams,
      ...params,
    });
  }
}

export function actionEvent(
  category: string,
  action: string,
  actionTarget: string
) {
  console.info("GTM", "actionEvent", { category, action, actionTarget });
  if (window.dataLayer) {
    window.dataLayer.push({
      event: "actionEvent",
      category,
      action,
      actionTarget,
    });
  }
}

export function purchaseEvent(
  order: Override<
    Pick<
      Order,
      | "incrementID"
      | "items"
      | "shippingAmount"
      | "currencyCode"
      | "subtotal"
      | "discountAmount"
      | "discountDescription"
    >,
    {
      items: Pick<
        OrderItem,
        "subtotal" | "qtyOrdered" | "name" | "sku" | "simpleName" | "simpleSku"
      >[];
    }
  >
) {
  const {
    incrementID,
    items,
    shippingAmount,
    currencyCode,
    subtotal,
    discountAmount,
    discountDescription,
  } = order;

  const revenue =
    subtotal - (discountAmount < 0 ? -discountAmount : discountAmount);

  const obj = {
    ecommerce: {
      currencyCode,
      purchase: {
        actionField: {
          id: incrementID,
          revenue,
          tax: "0.0000",
          shipping: numeral(shippingAmount).format("0.0000"),
          coupon: discountDescription || "",
        },
        products: items.map(item => {
          return {
            id: item.simpleSku || item.sku,
            name: item.simpleName || item.name,
            price: numeral(item.subtotal / item.qtyOrdered).format("0.0000"),
            quantity: numeral(item.qtyOrdered).format("0.0000"),
          };
        }),
      },
    },
  };

  console.info("GTM", "purchaseEvent", obj);

  if (window.dataLayer) {
    window.dataLayer.push({
      event: "purchase",
      ...obj,
    });
  }
}
